import { TdsDeclarationPill } from '../../components/domain/TdsDeclarationPill';
import { PayeeResolution } from './PayeeResolution';
import { orderAdvancePercentage, type OrderAdvanceTerms } from './advancePercentage';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  MoneyDecimal,
  OpenItemsResponse,
  PaymentOpenItemOrder,
  PaymentResponse,
  PayoutApproval,
  PayoutRequest,
} from '../../api/types';
import { mapPaymentError } from '../../api/endpoints/payments';
import { formatMoney, inputPaise, moneyInputError, paiseToDecimal, parsePaise } from './allocation';
import styles from './Payments.module.css';
import {
  useOpenItemsPaging,
  useSelectedOpenItems,
  type OpenItemsLoader,
} from './useOpenItemsPaging';
import { OpenItemsBrowser } from './OpenItemsBrowser';
import { OrderLabel } from './OrderLabel';
import { StatusChip } from './StatusChip';
import { usePartnerRemainder } from './usePartnerRemainder';
import { Button } from '../../components/ui/Button';
import { DialogHelp } from '../../help/DialogHelp';
import { isDemo } from '../../demo/flag';
import { useOpsUser } from '../ownership/opsUser';
import { useApprovalLimits } from './hooks';

interface PartnerOption {
  id: number;
  companyName: string;
  contactPerson: string;
}
interface PayoutRow {
  shipmentId: number;
  advance: string;
  balance: string;
  tdsAmount: string;
}
const money = (value: string | null | undefined) => parsePaise(value ?? '0.00');
const pod = (order: PaymentOpenItemOrder) =>
  order.podVerified === true ||
  order.advanceUnavailableReason === 'ADVANCE_AFTER_POD' ||
  order.status === 'COMPLETED';
const balanceReady = (order: PaymentOpenItemOrder) => order.balanceAvailable === true;
type EntryPurpose = 'ADVANCE' | 'BALANCE';
function dueRow(order: PaymentOpenItemOrder, purpose?: EntryPurpose): PayoutRow {
  return {
    shipmentId: order.shipmentId,
    advance: pod(order) || purpose === 'BALANCE' ? '0.00' : order.advanceAvailable,
    balance:
      balanceReady(order) && (pod(order) || purpose === 'BALANCE')
        ? order.balanceRemaining
        : '0.00',
    tdsAmount: '0.00',
  };
}
function hasValue(row: PayoutRow) {
  return (
    inputPaise(row.advance) > 0n ||
    inputPaise(row.balance) > 0n ||
    inputPaise(row.tdsAmount) > 0n ||
    Boolean(
      moneyInputError(row.advance.trim() || '0') ||
      moneyInputError(row.balance.trim() || '0') ||
      moneyInputError(row.tdsAmount.trim() || '0'),
    )
  );
}
function tdsOnly(row: PayoutRow) {
  return inputPaise(row.tdsAmount) > 0n && inputPaise(row.advance) + inputPaise(row.balance) === 0n;
}
/** Demo only (#297): an advance above the order's cap escalates with a reason instead of stopping. */
function overCap(order: PaymentOpenItemOrder, row: PayoutRow) {
  return inputPaise(row.advance) > money(order.advanceAvailable);
}
const REASON_MAX = 200;
function lineError(
  order: PaymentOpenItemOrder,
  row: PayoutRow,
  escalateOverCap = false,
): string | null {
  if (
    [row.advance, row.balance, row.tdsAmount].some((value) => moneyInputError(value.trim() || '0'))
  )
    return 'Enter a valid amount.';
  if (tdsOnly(row)) return 'Enter an amount for rows with TDS';
  const advance = inputPaise(row.advance),
    balance = inputPaise(row.balance),
    tds = inputPaise(row.tdsAmount);
  if (advance > 0n && pod(order)) return 'POD verified — pay as balance.';
  if (!escalateOverCap && advance + (balance > 0n ? 0n : tds) > money(order.advanceAvailable))
    return `Advance max ${formatMoney(money(order.advanceAvailable))}`;
  if (balance > 0n && !balanceReady(order))
    return order.balanceUnavailableReason === 'CHARGES_PENDING'
      ? 'Charges pending'
      : 'Available after POD';
  if (advance + balance + tds > money(order.partnerRemaining))
    return `Remaining max ${formatMoney(money(order.partnerRemaining))}`;
  return null;
}

type NewPayoutDialogProps = {
  openItems: OpenItemsResponse;
  partners?: PartnerOption[];
  initialShipmentId?: number;
  initialShipmentIds?: number[];
  onSelectPartner?: (partnerId: number) => void;
  onLoadItems?: OpenItemsLoader;
  onClose: () => void;
  /** Resolve with the created payout to show the server's approval routing (#297, demo only). */
  onSubmit: (request: PayoutRequest) => void | Promise<void | PaymentResponse | undefined>;
  submitting?: boolean;
  partnerAdvancePct?: number;
  advanceTerms?: OrderAdvanceTerms;
  initialPurpose?: EntryPurpose;
  payeeBlockReason?: string | null;
  entryScope?: 'SELECTED' | 'PARTNER';
};

export function NewPayoutDialog(props: NewPayoutDialogProps) {
  return isDemo() ? <DemoLimitedPayoutDialog {...props} /> : <PayoutDialog {...props} />;
}

/** Demo only until the build issue (#297): adds the signed-in user's approval limit from the server. */
function DemoLimitedPayoutDialog(props: NewPayoutDialogProps) {
  const { opsUser } = useOpsUser();
  const limits = useApprovalLimits();
  const makerLimit = limits.data?.users.find((u) => u.opsUserId === opsUser?.id)?.limit ?? null;
  return <PayoutDialog {...props} demoLimits={{ makerLimit }} />;
}

function PayoutDialog({
  openItems: initialItems,
  partners = [],
  initialShipmentId,
  initialShipmentIds = [],
  onSelectPartner,
  onLoadItems,
  onClose,
  onSubmit,
  submitting = false,
  advanceTerms,
  initialPurpose,
  payeeBlockReason,
  entryScope = initialShipmentId != null || initialShipmentIds.length ? 'SELECTED' : 'PARTNER',
  demoLimits,
}: NewPayoutDialogProps & { demoLimits?: { makerLimit: MoneyDecimal | null } }) {
  const { t } = useTranslation();
  const escalate = demoLimits != null;
  const paging = useOpenItemsPaging(initialItems, onLoadItems, [
    ...initialShipmentIds,
    ...(initialShipmentId == null ? [] : [initialShipmentId]),
  ]);
  const scoped = entryScope === 'SELECTED';
  const scopeKey = [
    ...initialShipmentIds,
    ...(initialShipmentId == null ? [] : [initialShipmentId]),
  ].join(',');
  const single = scoped && scopeKey.split(',').filter(Boolean).length === 1;
  const openItems = useMemo(
    () =>
      scoped
        ? {
            ...paging.items,
            orders: paging.items.orders.filter((order) =>
              scopeKey.split(',').includes(String(order.shipmentId)),
            ),
          }
        : paging.items,
    [paging.items, scoped, scopeKey],
  );
  const partnerRemainder = usePartnerRemainder(initialItems, !scoped);
  const requestId = useRef(globalThis.crypto.randomUUID());
  const initialKey = [
    ...initialShipmentIds,
    ...(initialShipmentId == null ? [] : [initialShipmentId]),
  ].join(',');
  const createRow = useCallback(
    (order: PaymentOpenItemOrder, previous?: PayoutRow): PayoutRow =>
      previous ??
      (initialKey.split(',').includes(String(order.shipmentId))
        ? dueRow(order, initialPurpose)
        : { shipmentId: order.shipmentId, advance: '0.00', balance: '0.00', tdsAmount: '0.00' }),
    [initialKey, initialPurpose],
  );
  const selection = useSelectedOpenItems(openItems, createRow, hasValue);
  const { setRows } = selection;
  const inScope = (id: number) => !scoped || scopeKey.split(',').includes(String(id));
  const rows = selection.rows.filter((row) => inScope(row.shipmentId));
  const [search, setSearch] = useState('');
  const [selectedPartner, setSelectedPartner] = useState<number | null>(
    initialKey ? openItems.counterpartyId : null,
  );
  const [tdsOpen, setTdsOpen] = useState<Record<number, boolean>>({});
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [reason, setReason] = useState('');
  const [sent, setSent] = useState<PayoutApproval | null>(null);
  const partnersFound = useMemo(
    () =>
      partners.filter((partner) =>
        `${partner.companyName} ${partner.contactPerson}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      ),
    [partners, search],
  );
  const total = rows.reduce(
    (sum, row) => sum + inputPaise(row.advance) + inputPaise(row.balance),
    0n,
  );
  const invalid = selection.orders.some((order) => {
    const row = rows.find((item) => item.shipmentId === order.shipmentId);
    return inScope(order.shipmentId) && row && lineError(order, row, escalate);
  });
  // Demo (#297): above the order's cap or the maker's own limit (both server values) → a reason is required.
  const aboveLimit =
    escalate &&
    (selection.orders.some((order) => {
      const row = rows.find((item) => item.shipmentId === order.shipmentId);
      return inScope(order.shipmentId) && row != null && overCap(order, row);
    }) ||
      (demoLimits.makerLimit != null && total > money(demoLimits.makerLimit)));
  const trimmedReason = reason.trim();
  const reasonMissing = aboveLimit && (!trimmedReason || trimmedReason.length > REASON_MAX);
  const ready = openItems.payee?.payoutReady ?? false;
  const update = (
    shipmentId: number,
    field: keyof Omit<PayoutRow, 'shipmentId'>,
    value: string,
  ) => {
    setRows(rows.map((row) => (row.shipmentId === shipmentId ? { ...row, [field]: value } : row)));
    setErrors((old) => ({ ...old, [shipmentId]: '' }));
    setSubmitError(null);
  };
  const displayMoney = (value: bigint) => formatMoney(value).replace(/\.00$/, '');
  const renderOrder = (order: PaymentOpenItemOrder) => {
    const pct = advanceTerms
      ? advanceTerms.shipmentId === order.shipmentId
        ? orderAdvancePercentage(advanceTerms, order.advanceCap)
        : undefined
      : undefined;
    const row = rows.find((item) => item.shipmentId === order.shipmentId);
    if (!row) return null;
    const validation = lineError(order, row, escalate);
    const advance = inputPaise(row.advance);
    const balance = inputPaise(row.balance);
    const tds = inputPaise(row.tdsAmount);
    const advanceTds = balance > 0n ? 0n : tds;
    const advanceLeft = money(order.advanceAvailable) - advance - advanceTds;
    const remaining = money(order.partnerRemaining) - advance - balance - tds;
    const balanceLeft = remaining - (advanceLeft > 0n ? advanceLeft : 0n);
    const showAdvance = !pod(order) && (money(order.advanceAvailable) > 0n || !balanceReady(order));
    const showBalance = balanceReady(order) || pod(order);
    const requesting = (amount: bigint, withheld: bigint, available: bigint, purpose: string) =>
      withheld > 0n
        ? `${displayMoney(amount + withheld)} incl. TDS ${displayMoney(withheld)} ${purpose}`
        : amount === available && purpose === 'advance' && pct != null
          ? `${displayMoney(amount)} advance (${pct} %)`
          : `${displayMoney(amount)}${amount < available ? ` of ${displayMoney(available)}` : ''} ${purpose}`;
    const sentence =
      showAdvance && showBalance
        ? `Requesting ${requesting(advance, advanceTds, money(order.advanceAvailable), 'advance')} + ${requesting(balance, balance > 0n ? tds : 0n, money(order.balanceRemaining), 'balance')}. After this: ${displayMoney(remaining)} left.`
        : showAdvance
          ? `Requesting ${requesting(advance, tds, money(order.advanceAvailable), 'advance')}. After this: ${advanceLeft === 0n ? 'advance fully requested · ' : `${displayMoney(advanceLeft)} advance + `}${displayMoney(balanceLeft)} balance left${!balanceReady(order) ? ' (payable after POD)' : ''}.`
          : `Requesting ${requesting(balance, tds, money(order.balanceRemaining), 'balance')}. After this: ${displayMoney(remaining)} balance left.`;
    const amountInput = (purpose: 'advance' | 'balance') => (
      <label className={styles.payoutAmount}>
        <span className={styles.badge}>
          {purpose === 'advance' ? (pct == null ? 'Advance' : `Advance ${pct} %`) : 'Balance'}
        </span>
        {t('payments.newPayoutDialog.amount')}
        <input
          className={styles.moneyInput}
          aria-label={`${purpose === 'advance' ? 'Advance' : 'Balance'} for #${order.shipmentId}`}
          disabled={
            submitting ||
            pending ||
            (purpose === 'advance' ? money(order.advanceAvailable) <= 0n : !balanceReady(order))
          }
          value={row[purpose]}
          onChange={(event) => update(order.shipmentId, purpose, event.target.value)}
        />
      </label>
    );
    return (
      <section
        key={order.shipmentId}
        aria-label={`Payout for #${order.shipmentId}`}
        className={styles.payoutOrder}
      >
        <div className={styles.payoutLine}>
          <OrderLabel {...order} newTab />
          {!single && hasValue(row) && !tdsOnly(row) && (
            <span className={styles.badge}>{t('payments.newPayoutDialog.selected')}</span>
          )}
          {showAdvance && amountInput('advance')}
          {showBalance && amountInput('balance')}
          <Button
            variant="secondary"
            size="sm"
            disabled={submitting || pending}
            onClick={() =>
              setRows(
                rows.map((item) =>
                  item.shipmentId === order.shipmentId
                    ? dueRow(
                        order,
                        initialKey.split(',').includes(String(order.shipmentId))
                          ? initialPurpose
                          : undefined,
                      )
                    : item,
                ),
              )
            }
          >
            {t('payments.newPayoutDialog.fill')}
          </Button>
        </div>
        {initialPurpose === 'ADVANCE' &&
          initialKey.split(',').includes(String(order.shipmentId)) &&
          pod(order) && (
            <p className={styles.subtle}>
              {t('payments.newPayoutDialog.podFolded')}
            </p>
          )}
        {!pod(order) && money(order.advanceAvailable) <= 0n && (
          <p className={styles.subtle}>
            {order.advanceUnavailableReason === 'AVAILABLE_AFTER_LOADING'
              ? 'Available after loading'
              : 'Advance fully requested'}
          </p>
        )}
        {!balanceReady(order) && order.balanceUnavailableReason === 'CHARGES_PENDING' && (
          <p className={styles.subtle}>{t('payments.newPayoutDialog.chargesPending')}</p>
        )}
        {escalate && (
          <p className={styles.subtle}>
            {t('payments.newPayoutDialog.advanceLimitLine', {
              cap: formatMoney(money(order.advanceCap)),
              paid: formatMoney(money(order.advancePaid)),
            })}
          </p>
        )}
        <p aria-live="polite">
          {single
            ? `${sentence.split('. After this:')[0]}. Remaining on this order after approval: ${displayMoney(remaining)}${showAdvance && !balanceReady(order) ? ' (balance payable after POD)' : ''}.`
            : sentence}
        </p>
        {(validation || errors[order.shipmentId]) && (
          <div role="alert" className={styles.error}>
            #{order.shipmentId}: {validation || errors[order.shipmentId]}
          </div>
        )}
        <div className={styles.payoutLine}>
          <details>
            <summary>{t('payments.newPayoutDialog.details')}</summary>
            <p>
              {t('payments.newPayoutDialog.partnerTotal', { amount: formatMoney(money(order.partnerTotal)) })} ·{' '}
              {pct == null ? 'Advance' : `${pct} % advance`} {formatMoney(money(order.advanceCap))}
            </p>
            <p>
              {t('payments.newPayoutDialog.paidCommittedLine', {
                paid: formatMoney(money(order.advancePaid) + money(order.balancePaid)),
                committed: formatMoney(money(order.advanceCommitted) + money(order.balanceCommitted)),
                remaining: formatMoney(money(order.partnerRemaining)),
              })}
            </p>
          </details>
          {tdsOpen[order.shipmentId] || tds > 0n ? (
            <label className={styles.payoutAmount}>
              {t('payments.newPayoutDialog.tds')}
              <input
                className={styles.moneyInput}
                aria-label={`TDS for #${order.shipmentId}`}
                disabled={submitting || pending}
                value={row.tdsAmount}
                onChange={(event) => update(order.shipmentId, 'tdsAmount', event.target.value)}
              />
            </label>
          ) : (
            <button
              className={styles.payoutTdsLink}
              disabled={submitting || pending}
              onClick={() => setTdsOpen((old) => ({ ...old, [order.shipmentId]: true }))}
            >
              {t('payments.newPayoutDialog.addTds')}
            </button>
          )}
        </div>
      </section>
    );
  };
  const submit = async () => {
    if (
      !ready ||
      invalid ||
      reasonMissing ||
      total <= 0n ||
      pending ||
      submitting ||
      paging.pending ||
      paging.prefillError ||
      (scoped && !scopeKey)
    )
      return;
    const allocations: PayoutRequest['allocations'] = [...selection.selected.values()].flatMap(
      ({ row }) => {
        if (!inScope(row.shipmentId)) return [];
        const lines: PayoutRequest['allocations'] = [];
        if (inputPaise(row.advance) > 0n)
          lines.push({
            shipmentId: row.shipmentId,
            purpose: 'ADVANCE',
            amount: paiseToDecimal(inputPaise(row.advance)),
            tdsAmount:
              inputPaise(row.balance) > 0n ? '0.00' : paiseToDecimal(inputPaise(row.tdsAmount)),
          });
        if (inputPaise(row.balance) > 0n)
          lines.push({
            shipmentId: row.shipmentId,
            purpose: 'BALANCE',
            amount: paiseToDecimal(inputPaise(row.balance)),
            tdsAmount: paiseToDecimal(inputPaise(row.tdsAmount)),
          });
        return lines;
      },
    );
    setPending(true);
    setSubmitError(null);
    setErrors({});
    try {
      const created = await onSubmit({
        clientRequestId: requestId.current,
        partnerId: openItems.counterpartyId,
        note: note.trim(),
        allocations,
        ...(escalate ? { reason: aboveLimit ? trimmedReason : null } : {}),
      });
      if (escalate && created && created.approval) setSent(created.approval);
    } catch (caught) {
      const error = caught as {
        code?: string;
        shipmentId?: number;
        response?: { data?: { code?: string; error?: string } };
      };
      const code = error.code ?? error.response?.data?.code;
      const message = mapPaymentError(caught);
      if (['ADVANCE_EXCEEDED', 'ADVANCE_AFTER_POD', 'BALANCE_BEFORE_POD'].includes(code ?? '')) {
        // Only extract a numeric identity from diagnostics, never display backend free text.
        const matched = error.response?.data?.error?.match(/(?:order\s+#?|on order\s+)(\d+)/i);
        const id = error.shipmentId ?? (matched ? Number(matched[1]) : undefined);
        const offending = allocations.filter((line) =>
          id != null
            ? line.shipmentId === id
            : line.purpose === (code === 'BALANCE_BEFORE_POD' ? 'BALANCE' : 'ADVANCE'),
        );
        setErrors(Object.fromEntries(offending.map((line) => [line.shipmentId, message])));
        if (!offending.length) setSubmitError(message);
      } else setSubmitError(message);
    } finally {
      setPending(false);
    }
  };
  const visible = selection.orders.filter(
    (order) => !scoped || scopeKey.split(',').includes(String(order.shipmentId)),
  );
  const scopeRemaining =
    scoped && !paging.pending && !paging.prefillError
      ? visible.reduce((sum, order) => sum + money(order.partnerRemaining), 0n)
      : undefined;
  if (sent)
    return (
      <div className={styles.scrim}>
        <section
          role="dialog"
          aria-modal="true"
          aria-label={t('payments.newPayoutDialog.requestedTitle')}
          className={`${styles.dialog} ${styles.compact}`}
        >
          <header className={styles.header}>
            <h2>{t('payments.newPayoutDialog.requestedTitle')}</h2>
          </header>
          <div className={styles.body}>
            <p role="status">
              {sent.state === 'ESCALATED' && sent.approverName
                ? `Goes to ${sent.approverName}${sent.approverLimit ? ` — limit ${formatMoney(money(sent.approverLimit))}` : ''}`
                : 'Sent for approval. Any approver can approve it.'}
            </p>
            {sent.state === 'ESCALATED' && sent.overCapBy && (
              <p className={styles.subtle}>
                {t('payments.newPayoutDialog.overCapBy', { amount: formatMoney(money(sent.overCapBy)) })}
              </p>
            )}
            <div className={styles.actions}>
              <Button variant="primary" size="sm" onClick={onClose}>
                {t('payments.newPayoutDialog.done')}
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  return (
    <div className={styles.scrim}>
      <section
        role="dialog"
        aria-modal="true"
        aria-label={t('payments.newPayoutDialog.dialogLabel')}
        className={`${styles.dialog} ${styles.payoutDialog} ${single ? styles.compact : ''}`}
      >
        <header className={styles.header}>
          <div>
            <h2>{t('payments.newPayoutDialog.title')}</h2>
            <p>{t('payments.newPayoutDialog.subtitle')}</p>
          </div>
          <DialogHelp context="new-payout" title={t('payments.newPayoutDialog.title')} />
        </header>
        <div className={styles.body}>
          {!scoped && !initialKey && partners.length > 0 && (
            <div className={styles.card}>
              <label className={styles.field}>
                {t('payments.newPayoutDialog.partnerLabel')}
                <input
                  className={styles.search}
                  aria-label={t('payments.newPayoutDialog.searchPartner')}
                  placeholder={t('payments.newPayoutDialog.searchPartnerPlaceholder')}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </label>
              <div className={styles.partnerList}>
                {partnersFound.map((partner) => (
                  <button
                    key={partner.id}
                    className={styles.partner}
                    onClick={() => {
                      setSelectedPartner(partner.id);
                      selection.clear();
                      onSelectPartner?.(partner.id);
                    }}
                  >
                    <strong>{partner.companyName}</strong>
                    <span>{partner.contactPerson}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {(initialKey || selectedPartner != null || partners.length === 0) && (
            <div
              role={ready ? undefined : 'alert'}
              className={ready ? styles.payee : styles.warning}
            >
              <div>
                <div className={styles.payeeTitle}>
                  {openItems.payee?.companyName ?? 'Payee unavailable'}
                </div>
                {!ready && (
                  <p>
                    {t('payments.newPayoutDialog.notReady')}{' '}
                    {(
                      {
                        NO_BANK: 'no bank details',
                        BANK_MISSING: 'no bank details',
                        KYC_PENDING: 'KYC pending',
                        KYC_REJECTED: 'KYC rejected',
                        NO_FLEET_MANAGER: 'no partner',
                        NO_PARTNER: 'no partner',
                      } as Record<string, string>
                    )[openItems.payee?.payoutBlockReason ?? payeeBlockReason ?? ''] ??
                      (!openItems.payee
                        ? 'no partner'
                        : openItems.payee.kycStatus !== 'VERIFIED'
                          ? `KYC ${openItems.payee.kycStatus.toLowerCase()}`
                          : 'payee details unavailable')}
                  </p>
                )}
                {!ready && (
                  <PayeeResolution
                    partnerId={openItems.counterpartyId}
                    reason={openItems.payee?.payoutBlockReason ?? payeeBlockReason}
                    kycStatus={openItems.payee?.kycStatus}
                  />
                )}
                {openItems.payee && (
                  <>
                    <div className={styles.payeeMeta}>
                      {t('payments.newPayoutDialog.contact')} <strong>{openItems.payee.contactPerson}</strong>
                    </div>
                    {openItems.payee.maskedAccount && (
                      <div className={styles.payeeMeta}>
                        {openItems.payee.maskedAccount} · {t('payments.newPayoutDialog.ifsc')} {openItems.payee.ifsc}
                      </div>
                    )}
                    <StatusChip displayStatus={`KYC_${openItems.payee.kycStatus}`} />
                    {openItems.payee.tdsDeclaration && (
                      <div className={styles.payeeMeta}>
                        <TdsDeclarationPill declaration={openItems.payee.tdsDeclaration} />
                      </div>
                    )}
                  </>
                )}
                {initialKey && (
                  <div className={styles.payeeMeta}>{t('payments.newPayoutDialog.payeeIsFleetManager')}</div>
                )}
              </div>
            </div>
          )}
          <div className={single ? undefined : styles.card}>
            {!single && (
              <div className={styles.sectionTitle}>
                <div>
                  <h3>{t('payments.newPayoutDialog.allocateTitle')}</h3>
                  <p>
                    {t('payments.newPayoutDialog.allocateHint')}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={pending || submitting}
                  onClick={() =>
                    setRows(
                      selection.orders.map((order) =>
                        !initialKey || initialKey.split(',').includes(String(order.shipmentId))
                          ? dueRow(order, initialPurpose)
                          : (rows.find((row) => row.shipmentId === order.shipmentId) ?? {
                              shipmentId: order.shipmentId,
                              advance: '0.00',
                              balance: '0.00',
                              tdsAmount: '0.00',
                            }),
                      ),
                    )
                  }
                >
                  {initialKey ? 'Fill selected orders due' : 'Fill all due'}
                </Button>
              </div>
            )}
            {!scoped && <OpenItemsBrowser paging={paging} />}
            {scoped && paging.pending && <p role="status">{t('payments.newPayoutDialog.loadingSelected')}</p>}
            {paging.prefillError && (
              <div role="alert" className={styles.error}>
                {paging.prefillError}
              </div>
            )}
            <div className={styles.payoutOrders}>{visible.map(renderOrder)}</div>
            {submitError && (
              <div role="alert" className={styles.error}>
                {submitError}
              </div>
            )}
            {aboveLimit && (
              <div className={styles.warning}>
                <p>{t('payments.newPayoutDialog.aboveLimit')}</p>
                <label className={`${styles.field} ${styles.full}`}>
                  {t('payments.newPayoutDialog.aboveLimitReason')}
                  <textarea
                    rows={2}
                    maxLength={REASON_MAX}
                    disabled={pending || submitting}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  />
                </label>
              </div>
            )}
            <label className={`${styles.field} ${styles.full}`}>
              {t('payments.newPayoutDialog.auditNote')}
              <textarea
                rows={2}
                maxLength={500}
                disabled={pending || submitting}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </label>
          </div>
        </div>
        <footer className={styles.payoutFooter}>
          {!single && (
            <div
              className={styles.card}
              role="region"
              aria-label={t('payments.newPayoutDialog.summaryLabel')}
              aria-live="polite"
            >
              <strong>
                {t('payments.newPayoutDialog.requestingTotal', {
                  amount: displayMoney(total),
                  name: openItems.payee?.companyName ?? t('payments.newPayoutDialog.thisPartner'),
                })}
              </strong>
              <div className={styles.payoutSummaryLines}>
                {[...selection.selected.values()]
                  .filter(({ row }) => inScope(row.shipmentId))
                  .map(({ row }) => (
                    <div key={row.shipmentId}>
                      #{row.shipmentId} ·{' '}
                      {inputPaise(row.advance) > 0n && (
                        <>{t('payments.newPayoutDialog.summaryAdvance', { amount: displayMoney(inputPaise(row.advance)) })}</>
                      )}
                      {inputPaise(row.advance) > 0n && inputPaise(row.balance) > 0n && ' · '}
                      {inputPaise(row.balance) > 0n && (
                        <>{t('payments.newPayoutDialog.summaryBalance', { amount: displayMoney(inputPaise(row.balance)) })}</>
                      )}
                    </div>
                  ))}
              </div>
              <p>
                {scoped
                  ? 'Remaining on selected orders after approval:'
                  : 'Still owed to this partner after approval:'}{' '}
                {(scoped ? scopeRemaining : partnerRemainder.total) == null
                  ? partnerRemainder.failed
                    ? 'Unavailable — close and retry to refresh the partner total.'
                    : 'Loading…'
                  : displayMoney(
                      (scoped ? scopeRemaining! : partnerRemainder.total!) -
                        total -
                        rows.reduce((sum, row) => sum + inputPaise(row.tdsAmount), 0n),
                    )}
              </p>
            </div>
          )}
          <div className={styles.actions}>
            <Button
              variant="secondary"
              size="sm"
              disabled={pending || submitting}
              onClick={onClose}
            >
              {t('common.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={
                !ready ||
                total <= 0n ||
                invalid ||
                reasonMissing ||
                pending ||
                submitting ||
                paging.pending ||
                Boolean(paging.prefillError) ||
                (scoped && !scopeKey)
              }
              onClick={() => void submit()}
            >
              {pending || submitting
                ? 'Requesting…'
                : total <= 0n
                  ? 'Enter an amount'
                  : `Request ${displayMoney(total)} payout`}
            </Button>
          </div>
        </footer>
      </section>
    </div>
  );
}
